import { z } from "zod";
const IncentiveProviderType = {
  Amazon: "AMAZON"
};
function getRatingsFromSurveyScale(surveyScale) {
  switch (surveyScale) {
    case "important_unimportant":
      return [
        {
          name: "Not at all important",
          rating: 1
        },
        {
          name: "Not so important",
          rating: 2
        },
        {
          name: "Somewhat important",
          rating: 3
        },
        {
          name: "Very important",
          rating: 4
        },
        {
          name: "Extremely important",
          rating: 5
        }
      ];
    case "agree_disagree":
      return [
        {
          name: "Strongly disagree",
          rating: 1
        },
        {
          name: "Disagree",
          rating: 2
        },
        {
          name: "Neutral",
          rating: 3
        },
        {
          name: "Agree",
          rating: 4
        },
        {
          name: "Strongly agree",
          rating: 5
        }
      ];
    case "satisfied_dissatisfied":
      return [
        {
          name: "Very dissatisfied",
          rating: 1
        },
        {
          name: "Dissatisfied",
          rating: 2
        },
        {
          name: "Neutral",
          rating: 3
        },
        {
          name: "Satisfied",
          rating: 4
        },
        {
          name: "Very satisfied",
          rating: 5
        }
      ];
    default:
      return [];
  }
}
const ComplexQuestionTypeToString = {
  rating: "Rating scale",
  multiple_choice: "Multiple choice"
};
const TrendDataType = {
  SENTIMENT: "sentiment"
};
const PosthogEventType = {
  ProjectCreate: "projects_list:project_create",
  ProjectLaunch: "project_configuration:project_launch",
  ParticipantsInvite: "project_configuration:participants_invite",
  TeammatesInvite: "company_settings:teammates_invite",
  TeammateDomainLogin: "signup_flow:teammate_domainlogin",
  InterviewStart: "interview_app:interview_start",
  InterviewEnd: "interview_app:interview_end",
  TrialContactUs: "trial:contactus_click",
  /** @deprecated Superseded by InterviewError with interview_error_type = unsupported_browser. */
  InterviewUnsupportedBrowser: "interview_app:interview_unsupported_browser",
  /** The interview error page was shown. */
  InterviewError: "interview_app:interview_error",
  /** The interview onboarding form was shown. */
  InterviewOnboardingStarted: "interview_app:interview_onboarding_started",
  /** The user submitted the interview onboarding form. */
  InterviewOnboardingSubmitted: "interview_app:interview_onboarding_submitted",
  /** The reCAPTCHA was verified after the user submitted the onboarding form. */
  InterviewOnboardingVerified: "interview_app:interview_onboarding_verified",
  /** An error occurred trying to set up microphone and/or camera. */
  InterviewDeviceError: "interview_app:device_error",
  /** The voice agent interviewer was shown. */
  InterviewStarted: "interview_app:interview_started",
  /** Microphone and/or camera have been set up. */
  InterviewDevicesSetup: "interview_app:devices_setup",
  /** An interview call is starting. */
  InterviewCallInitiating: "interview_app:call_initiating",
  /** An interview call has been initiated. */
  InterviewCallInitiated: "interview_app:call_initiated",
  /** An interview call has started. */
  InterviewCallStarted: "interview_app:call_started",
  /** An interview call has stopped. */
  InterviewCallEnded: "interview_app:call_ended",
  /** The interview completion page was shown. */
  InterviewCompleted: "interview_app:interview_completed",
  /** We started processing the Retell call. Used by the API on behalf of the interview app. */
  RetellCallProcessing: "interview_app:retell_call_processing",
  /** We started processing the interview insights. Used by the Lambda on behalf of the interview app. */
  InterviewInsightsProcessing: "interview_app:insights_processing",
  /** @deprecated Used by defunct "Rate this interview" feature. */
  InterviewFeedbackSubmit: "interview_slideover:feedback_submit",
  /** Feedback submitted when confirming or updating an assessment score. */
  InterviewAssessmentFeedbackSubmit: "interview_slideover:assessment_feedback_submit",
  InterviewEmptyAudioResponse: "interview_app:empty_audio_response",
  CallFailedToInitialize: "interview_app:call_failed_to_initialize",
  CompletedInterviewProcessed: "completed_interview_processed",
  TeammateAgentAnonymousCaller: "teammate_agent:anonymous_caller",
  /** A resolved coaching-goal view was rendered on a teammate overview. */
  TeammateCoachingGoalView: "teammate_coaching:goal_view",
  /** A collapsed weekly coaching note was expanded by the viewer. */
  TeammateCoachingNoteExpand: "teammate_coaching:note_expand",
  /**
   * A call was opened from a coaching goal's trend-meter popover.
   *
   * Counts primary-button and modifier (cmd/ctrl) clicks, which both fire
   * `click` on the link. Middle-click fires `auxclick` instead, and
   * right-click -> "Open in new tab" fires no handler at all, so this is a
   * lower bound on opens — read it as a relative engagement measure, not an
   * exact count.
   */
  TeammateCoachingCallOpen: "teammate_coaching:call_open",
  /** A grading quote or reference was opened from coaching content. Undercounts the same way as {@link TeammateCoachingCallOpen}. */
  TeammateCoachingQuoteOpen: "teammate_coaching:quote_open",
  /** A manager note or next-week goal was saved successfully. */
  TeammateCoachingManagerInputSave: "teammate_coaching:manager_input_save",
  /** A manager note or next-week goal was deleted successfully. */
  TeammateCoachingManagerInputDelete: "teammate_coaching:manager_input_delete",
  /** A Goal progress status was selected in the team-performance filter. */
  TeamPerformanceCoachingGoalFilterApply: "team_performance:coaching_goal_filter_apply",
  /**
   * A coaching email (rep Monday/Friday or manager digest) was successfully
   * handed to Postmark. Captured server-side in the success branch of
   * `sendTeammateNotification`, never on its duplicate/in-flight path.
   */
  CoachingEmailSent: "coaching_email:sent",
  /**
   * Postmark's Open webhook reported the recipient opened a coaching email.
   * Captured server-side by the Postmark webhook handler.
   */
  CoachingEmailOpen: "coaching_email:open",
  /**
   * Postmark's Click webhook reported the recipient clicked a tracked link in
   * a coaching email. Captured server-side by the Postmark webhook handler.
   */
  CoachingEmailClick: "coaching_email:click"
};
const basicQuestionSchema = z.object({
  question: z.string()
});
const TranscriptFragmentSaveStatus = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error"
};
const LoadingState = {
  LOADING: "loading",
  LOADED: "loaded",
  ERROR: "error"
};
const InterviewDeviceType = {
  MICROPHONE: "microphone",
  CAMERA: "camera"
};
export {
  ComplexQuestionTypeToString,
  IncentiveProviderType,
  InterviewDeviceType,
  LoadingState,
  PosthogEventType,
  TranscriptFragmentSaveStatus,
  TrendDataType,
  basicQuestionSchema,
  getRatingsFromSurveyScale
};
//# sourceMappingURL=client.js.map
